// Personal bookmarks: per-user pins from /api/bookmarks, rendered from a
// client GeoJSON source. They are private, so they never ride the shared
// entity tiles — and being registered on the one persistent style, they
// survive basemap switches like every other overlay.
//
// Whether they DRAW is a per-browser display choice (panels.ts owns the
// switches and their storage): the whole layer on or off, plus a set of
// single pins hidden without being deleted.

import type { Feature } from 'geojson';
import * as maplibregl from 'maplibre-gl';
import type { BookmarkDto } from './api';
import { drawChip } from './objects';

const SRC = 'user-bookmarks';
const COLOR = '#2f6fed';
const LAYER_IDS = ['user-bookmarks-outline', 'user-bookmarks-pin', 'user-bookmarks-label'];

// The panels load bookmarks and restore the switches while the map style may
// still be booting; keep the latest of each so installing the layers can
// catch up.
let latest: BookmarkDto[] = [];
let visible = true;
let hidden: ReadonlySet<string> = new Set();

export function installUserBookmarkLayers(map: maplibregl.Map): void {
  map.addSource(SRC, {
    type: 'geojson', data: { type: 'FeatureCollection', features: [] },
  });
  if (!map.hasImage('bookmark-pin')) {
    map.addImage('bookmark-pin', drawChip(COLOR, 'bookmark'), { pixelRatio: 2 });
  }
  map.addLayer({
    id: 'user-bookmarks-outline', type: 'line', source: SRC,
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: { 'line-color': COLOR, 'line-width': 1.6, 'line-dasharray': [3, 2] },
  });
  map.addLayer({
    id: 'user-bookmarks-pin', type: 'symbol', source: SRC,
    filter: ['==', ['geometry-type'], 'Point'],
    layout: {
      'icon-image': 'bookmark-pin',
      'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.6, 12, 0.8] as never,
      'icon-allow-overlap': true,
    },
  });
  map.addLayer({
    id: 'user-bookmarks-label', type: 'symbol', source: SRC, minzoom: 7,
    filter: ['==', ['geometry-type'], 'Point'],
    layout: {
      'text-field': ['get', 'name'] as never,
      'text-font': ['Noto Sans Bold'],   // OpenFreeMap serves Noto only; Open Sans 404s
      'text-size': 10.5,
      'text-offset': [0, 1.4],
      'text-anchor': 'top',
      'text-optional': true,
    },
    paint: {
      'text-color': COLOR,
      'text-halo-color': 'rgba(255,255,255,.88)',
      'text-halo-width': 1.3,
    },
  });
  applyVisibility(map);
  push(map);
}

export function setUserBookmarks(map: maplibregl.Map, bookmarks: BookmarkDto[]): void {
  latest = bookmarks;
  push(map);
}

/** The whole layer on or off — every pin, outline and label at once. */
export function setUserBookmarksVisible(map: maplibregl.Map, on: boolean): void {
  visible = on;
  applyVisibility(map);
}

/** Pins hidden one by one: still bookmarks, still listed, just not drawn.
 *  The set is read on every push, so pass it again after changing it. */
export function setHiddenUserBookmarks(map: maplibregl.Map, ids: ReadonlySet<string>): void {
  hidden = ids;
  push(map);
}

function applyVisibility(map: maplibregl.Map): void {
  for (const id of LAYER_IDS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
  }
}

function push(map: maplibregl.Map): void {
  const source = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
  if (!source) return;
  const features: Feature[] = latest.filter((b) => !hidden.has(b.id)).map((b) => ({
    type: 'Feature',
    geometry: b.geometry,
    properties: { id: b.id, name: b.name },
  }));
  source.setData({ type: 'FeatureCollection', features });
}
